import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export interface TaskTemplateTrigger {
  id: number;
  task_template_id: number;
  trigger_event: 'case_created' | 'stage_changed';
  trigger_stage_id: number | null;
  case_type_id: number | null;
  stage_name: string | null;
  active: boolean;
  created_at: string;
}

export interface TaskTemplate {
  id: number;
  name: string;
  description: string;
  category: string;
  default_assignee_strategy: 'case_owner' | 'specific_user';
  default_assignee_user_id: string | null;
  deadline_offset_days: number;
  activity_code_id: number | null;
  activity_code: string | null;
  activity_code_name: string | null;
  billable_default: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplateInput {
  name: string;
  description: string;
  category?: string;
  default_assignee_strategy?: string;
  default_assignee_user_id?: string | null;
  deadline_offset_days?: number;
  activity_code_id?: number | null;
  billable_default?: boolean;
}

export interface AddTriggerInput {
  trigger_event: 'case_created' | 'stage_changed';
  trigger_stage_id?: number | null;
  case_type_id?: number | null;
}

const QUERY_KEY = 'task-templates';
const TRIGGERS_KEY = (id: number) => ['task-template-triggers', id] as const;

export function useTaskTemplates(active = true) {
  return useQuery({
    queryKey: [QUERY_KEY, { active }],
    queryFn: async () => {
      const res = await api.get<{ items: TaskTemplate[] }>(`/api/task-templates?active=${active}`);
      return res.data.items ?? [];
    },
    staleTime: 60_000,
  });
}

export function useTaskTemplateTriggers(templateId: number) {
  return useQuery({
    queryKey: TRIGGERS_KEY(templateId),
    queryFn: async () => {
      const res = await api.get<{ items: TaskTemplateTrigger[] }>(
        `/api/task-templates/${templateId}/triggers`,
      );
      return res.data.items ?? [];
    },
    enabled: templateId > 0,
    staleTime: 60_000,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTemplateInput) =>
      api.post('/api/task-templates', data).then((r) => r.data.item as TaskTemplate),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<CreateTemplateInput> & { active?: boolean };
    }) => api.patch(`/api/task-templates/${id}`, patch).then((r) => r.data.item as TaskTemplate),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/task-templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useAddTrigger(templateId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddTriggerInput) =>
      api.post(`/api/task-templates/${templateId}/triggers`, data).then((r) => r.data.item),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRIGGERS_KEY(templateId) }),
  });
}

export function useRemoveTrigger(templateId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (triggerId: number) =>
      api.delete(`/api/task-templates/${templateId}/triggers/${triggerId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRIGGERS_KEY(templateId) }),
  });
}
